
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { getPaypalSubscriptionStatus, cancelPaypalSubscription } from '../services/paypalService';
import type { PayPalSubscriptionDetails as PayPalSubscriptionDetailsType } from '../services/paypalService';
import { Button } from './ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Loader2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface PayPalDetailsProps {
  subscriptionId: string;
  onCancelled: () => void;
}

export function PayPalSubscriptionDetails({ subscriptionId, onCancelled }: PayPalDetailsProps) {
  const [details, setDetails] = useState<PayPalSubscriptionDetailsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasCleanedUp, setWasCleanedUp] = useState(false);
  const { t } = useTranslation();

  const fetchDetails = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setWasCleanedUp(false);
    
    try {
      console.log("Fetching PayPal subscription details for:", subscriptionId);
      const data = await getPaypalSubscriptionStatus(subscriptionId);
      setDetails(data);
      console.log("PayPal subscription details loaded successfully");
    } catch (error: any) {
      console.error("Error fetching subscription details:", error);
      
      // Check if the error indicates the subscription was cleaned up
      if (error.message?.includes('cleaned') || error.message?.includes('not found')) {
        setWasCleanedUp(true);
        setError("The subscription was not found in PayPal and your account has been updated to the free tier.");
        
        // Trigger parent component refresh
        setTimeout(() => {
          onCancelled();
        }, 3000);
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to load subscription details";
        setError(errorMessage);
      }
      
      toast.error("Failed to load subscription details", {
        description: error instanceof Error ? error.message : "Unknown error occurred"
      });
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionId, onCancelled]);

  useEffect(() => {
    if (subscriptionId) {
      fetchDetails();
    }
  }, [fetchDetails, subscriptionId]);

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      console.log("Cancelling PayPal subscription:", subscriptionId);
      await cancelPaypalSubscription(subscriptionId);
      toast.success("Subscription cancelled successfully");
      onCancelled();
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to cancel subscription";
      toast.error("Failed to cancel subscription", {
        description: errorMessage
      });
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-6">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">{t('subscription.loadingSubscriptionDetails')}</span>
        </CardContent>
      </Card>
    );
  }

  if (wasCleanedUp) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Subscription Updated</h3>
            <p className="text-gray-600 mb-4">
              Your PayPal subscription was not found and your account has been updated to the free tier. 
              This page will refresh automatically.
            </p>
            <div className="animate-pulse text-sm text-gray-500">
              Refreshing...
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchDetails} variant="outline">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!details) {
    return (
      <Card>
        <CardContent className="py-6">
          <p>{t('subscription.unableToLoadDetails')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('subscription.paypalSubscription')}</CardTitle>
        <CardDescription>{t('subscription.paypalSubscriptionDetails')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium">{t('subscription.status')}</p>
            <p className="text-sm">
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                details.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                details.status === 'SUSPENDED' ? 'bg-yellow-100 text-yellow-800' : 
                'bg-red-100 text-red-800'
              }`}>
                {details.status}
              </span>
            </p>
          </div>
          <div>
            <p className="text-sm font-medium">{t('subscription.email')}</p>
            <p className="text-sm">{details.payer?.email_address || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium">{t('subscription.startDate')}</p>
            <p className="text-sm">{details.start_time ? format(new Date(details.start_time), 'PP') : 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium">{t('subscription.nextBilling')}</p>
            <p className="text-sm">{details.next_billing_time ? format(new Date(details.next_billing_time), 'PP') : 'N/A'}</p>
          </div>
          {details.last_payment && (
            <>
              <div>
                <p className="text-sm font-medium">{t('subscription.lastPayment')}</p>
                <p className="text-sm">{format(new Date(details.last_payment.time), 'PP')}</p>
              </div>
              <div>
                <p className="text-sm font-medium">{t('subscription.amount')}</p>
                <p className="text-sm">
                  {details.last_payment.amount.value} {details.last_payment.amount.currency_code}
                </p>
              </div>
            </>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={details.status !== 'ACTIVE' || isCancelling}>
              {isCancelling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('subscription.cancelSubscription')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('subscription.areYouSure')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('subscription.cancelSubscriptionWarning')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelSubscription}>
                {t('subscription.yesCancelSubscription')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardFooter>
    </Card>
  );
}
